
import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { PrintRun, Book, Warehouse, Items, InventoryMovement, InventoryMovementItem, PrintRunHistory, User } from "@/api/entities";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import {
  Plus,
  Search,
  Printer,
  Package,
  CheckCircle,
  Clock,
  Truck,
  Eye,
  Edit
} from "lucide-react";
import { format } from "date-fns";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Link } from "react-router-dom"; // Keep this as it might be used elsewhere or planned for future use, but it's no longer used for printRun details navigation
import { createPageUrl } from "@/utils";

import PrintRunForm from "../components/printRuns/PrintRunForm";
import EditPrintRunModal from "../components/printRuns/EditPrintRunModal";
import ViewPrintRunModal from "../components/printRuns/ViewPrintRunModal";
import { useLocalization } from "../components/providers/LocalizationProvider";

const statusColors = {
  planned: 'bg-gray-100 text-gray-800 border-gray-200',
  in_progress: 'bg-blue-100 text-blue-800 border-blue-200',
  completed: 'bg-green-100 text-green-800 border-green-200',
  delivered: 'bg-green-100 text-green-800 border-green-200',
  cancelled: 'bg-red-100 text-red-800 border-red-200',
  // Для обратной совместимости
  draft: 'bg-gray-100 text-gray-800 border-gray-200',
  in_production: 'bg-blue-100 text-blue-800 border-blue-200'
};

const statusIcons = {
  planned: Clock,
  in_progress: Printer,
  completed: CheckCircle,
  delivered: CheckCircle,
  cancelled: Package,
  // Для обратной совместимости
  draft: Clock,
  in_production: Printer
};



const typeLabels = {
    main_print: 'Основной тираж',
    reprint: 'Допечатка'
};

export default function PrintRuns() {
  const { t } = useTranslation();
  const { currencySymbol } = useLocalization();
  const [printRuns, setPrintRuns] = useState([]);
  const [books, setBooks] = useState([]);
  const [warehouses, setWarehouses] = useState([]);
  const [filteredPrintRuns, setFilteredPrintRuns] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedPrintRun, setSelectedPrintRun] = useState(null); // Unused, keeping for consistency if it's used elsewhere
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [user, setUser] = useState(null);
  
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [editingPrintRun, setEditingPrintRun] = useState(null);

  const [isViewModalOpen, setIsViewModalOpen] = useState(false);
  const [viewingPrintRun, setViewingPrintRun] = useState(null);

  const fieldLabels = {
    document_number: t('print_run_form.document_number'),
    document_date: t('print_run_form.document_date'),
    type: t('print_run_form.type'),
    book_id: t('print_run_form.book'),
    isbn: t('print_run_form.isbn'),
    quantity: t('print_run_form.quantity'),
    cost_per_unit: t('print_run_form.cost_per_unit'),
    warehouse_id: t('print_run_form.warehouse'),
    expected_delivery_date: t('print_run_form.expected_delivery_date'),
    notes: t('print_run_form.notes'),
  };

  useEffect(() => {
    loadData();
    loadUser();
  }, []);

  useEffect(() => {
    filterPrintRuns();
  }, [printRuns, searchTerm]);

  const loadUser = async () => {
    try {
      const currentUser = await User.me();
      setUser(currentUser);
    } catch (error) {
      console.warn('Could not load current user:', error);
    }
  };

  const loadData = async () => {
    setIsLoading(true);
    try {
      const [printRunsData, booksData, warehousesData] = await Promise.all([
        PrintRun.list('-created_date'),
        Book.list(),
        Warehouse.list({ is_active: true })
      ]);

      setPrintRuns(printRunsData || []);
      setBooks(booksData || []);
      setWarehouses(warehousesData || []);
    } catch (error) {
      console.error("Error loading data:", error);
      // Set empty arrays to prevent crashes
      setPrintRuns([]);
      setBooks([]);
      setWarehouses([]);
    }
    setIsLoading(false);
  };

  const filterPrintRuns = () => {
    let filtered = printRuns;

    if (searchTerm) {
              filtered = filtered.filter(printRun => {
          const book = books.find(b => b.id === printRun.book_id);
          
          return (
            printRun.document_number?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            printRun.isbn?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            book?.title?.toLowerCase().includes(searchTerm.toLowerCase())
          );
        });
    }

    setFilteredPrintRuns(filtered);
  };

  const handlePrintRunCreate = async (printRunData) => {
    try {
      // Устанавливаем статус по умолчанию
      const dataWithStatus = {
        ...printRunData,
        status: 'planned' // Используем новый статус вместо 'draft'
      };
      
      const newRun = await PrintRun.create(dataWithStatus);
      
      // Создаем историю тиража (опционально)
      if (user) {
          try {
                        await PrintRunHistory.create({
              print_run_id: newRun.id,
              user_name: user.full_name,
              action: 'created',
              details: `Тираж #${newRun.document_number || newRun.id} создан`
          });
          } catch (historyError) {
              console.warn('Failed to create print run history:', historyError);
              // История не критична, продолжаем работу
          }
      }
      
      await loadData();
      setShowCreateModal(false);
    } catch (error) {
      console.error("Error creating print run:", error);
      alert("Произошла ошибка при создании тиража");
    }
  };

  const handlePrintRunUpdate = async (printRunId, updates) => {
    try {
      await PrintRun.update(printRunId, updates);
      await loadData();
    } catch (error) {
      console.error("Error updating print run:", error);
      alert("Произошла ошибка при обновлении тиража");
    }
  };

  const handleStatusChange = async (printRunId, newStatus) => {
    const printRun = printRuns.find(p => p.id === printRunId);
    if (!printRun) return;

    const oldStatus = printRun.status;
    const statusLabels = {
        planned: 'Запланирован',
        in_progress: 'В печати',
        completed: 'Завершен',
        delivered: 'Доставлен',
        cancelled: 'Отменен',
        // Для обратной совместимости
        draft: 'Черновик',
        in_production: 'В печати'
    };

    const updates = { status: newStatus };

    // Если статус меняется на "delivered", создаем приходную накладную
    if (newStatus === 'delivered') {
      updates.actual_delivery_date = new Date().toISOString().split('T')[0];

      try {
        // Создаем приходную накладную
        const today = new Date();
        const year = today.getFullYear();
        const month = String(today.getMonth() + 1).padStart(2, '0');
        const day = String(today.getDate()).padStart(2, '0');
        const hours = String(today.getHours()).padStart(2, '0');
        const minutes = String(today.getMinutes()).padStart(2, '0');
        
        // Находим книгу и создаем издание если нужно
        const bookId = printRun.book_id;
        const book = books.find(b => b.id === bookId);
        if (!book) {
          throw new Error('Книга не найдена');
        }

        // Создаем или находим издание книги
        let bookEdition;
        if (printRun.isbn) {
          // Ищем существующее издание с таким ISBN
          const existingEditions = await Items.list({ type: 'book_edition', isbn: printRun.isbn });
          if (existingEditions.length > 0) {
            bookEdition = existingEditions[0];
          } else {
            // Создаем новое издание с заполнением метаданных
            const bookTitle = book?.title || printRun.book_title || 'Книга';
            const author = book?.author || undefined;
            bookEdition = await Items.create({
              type: 'book_edition',
              name: `${bookTitle} - ${printRun.edition_name || 'Основное издание'}`,
              // Дублируем ключевые поля в плоские колонки для DAL
              isbn: printRun.isbn,
              edition_name: printRun.edition_name || 'Основное издание',
              is_active: true,
              // Основные данные в metadata
              metadata: {
                book_id: bookId,
                isbn: printRun.isbn,
                edition_name: printRun.edition_name || 'Основное издание',
                author,
                title: bookTitle,
                format: printRun.format || undefined,
                pages: printRun.pages || undefined,
                cover_image_url: printRun.cover_image_url || undefined
              }
            });
          }
        } else {
          // Для допечатки наследуем ISBN из основного тиража
          if (printRun.parent_print_run_id) {
            const parentPrintRun = printRuns.find(p => p.id === printRun.parent_print_run_id);
            if (parentPrintRun && parentPrintRun.isbn) {
              const existingEditions = await Items.list({ type: 'book_edition', isbn: parentPrintRun.isbn });
              if (existingEditions.length > 0) {
                bookEdition = existingEditions[0];
              }
            }
          }
          
          // Если издание не найдено, создаем без ISBN
          if (!bookEdition) {
            const bookTitle = book?.title || printRun.book_title || 'Книга';
            const author = book?.author || undefined;
            bookEdition = await Items.create({
              type: 'book_edition',
              name: `${bookTitle} - ${printRun.edition_name || 'Допечатка'}`,
              // Без ISBN в плоских полях
              edition_name: printRun.edition_name || 'Допечатка',
              is_active: true,
              metadata: {
                book_id: bookId,
                edition_name: printRun.edition_name || 'Допечатка',
                author,
                title: bookTitle,
                format: printRun.format || undefined,
                pages: printRun.pages || undefined,
                cover_image_url: printRun.cover_image_url || undefined
              }
            });
          }
        }

        // Создаем приходную накладную
        const receiptMovement = {
          document_number: `R-${year}${month}${day}-${hours}${minutes}`,
          document_date: today.toISOString().split('T')[0],
          document_type: 'receipt',
          type: 'in',
          warehouse_id: printRun.warehouse_id,
          items: [{
            item_id: bookEdition.id,
            quantity: printRun.quantity
          }],
          notes: `Приходная накладная по тиражу ${printRun.document_number || printRun.id}`
        };

        await InventoryMovement.create(receiptMovement);
        console.log('Приходная накладная создана для тиража:', printRun.id);
      } catch (error) {
        console.error('Ошибка создания приходной накладной:', error);
        alert('Ошибка создания приходной накладной: ' + error.message);
        return; // Не меняем статус если не удалось создать накладную
      }
    }

    try {
      await handlePrintRunUpdate(printRunId, updates);
      if (user) {
          await PrintRunHistory.create({
              print_run_id: printRunId,
              user_name: user.full_name,
              action: 'status_changed',
              details: `Статус изменен с "${statusLabels[oldStatus] || oldStatus}" на "${statusLabels[newStatus] || newStatus}"`,
              old_value: oldStatus,
              new_value: newStatus
          });
      }
    } catch (error) {
      console.error("Error changing status:", error);
      alert("Произошла ошибка при изменении статуса");
    }
  };
  
  const handleEditClick = (printRun) => {
    setEditingPrintRun(printRun);
    setIsEditModalOpen(true);
  };

  const handleViewClick = (printRun) => {
    setViewingPrintRun(printRun);
    setIsViewModalOpen(true);
  };

  const handleEditFromView = (printRun) => {
    setIsViewModalOpen(false);
    setViewingPrintRun(null); // Clear viewing print run
    setEditingPrintRun(printRun);
    setIsEditModalOpen(true);
  };
  
  const handleSaveChanges = async (updatedData) => {
    if (!user || !editingPrintRun) return;

    const originalData = editingPrintRun;

    const changes = [];
    for (const key in updatedData) {
        if (updatedData[key] !== originalData[key] && fieldLabels[key]) {
            let oldValue = originalData[key];
            let newValue = updatedData[key];

            if (key === 'book_id') {
                oldValue = books.find(b => b.id === oldValue)?.title || oldValue;
                newValue = books.find(b => b.id === newValue)?.title || newValue;
            } else if (key === 'warehouse_id') {
                oldValue = warehouses.find(w => w.id === oldValue)?.name || oldValue;
                newValue = warehouses.find(w => w.id === newValue)?.name || newValue;
            } else if (key === 'type') {
                oldValue = oldValue === 'main_print' ? t('print_run_form.main_print') : t('print_run_form.reprint');
                newValue = newValue === 'main_print' ? t('print_run_form.main_print') : t('print_run_form.reprint');
            } else if (key === 'document_date' || key === 'expected_delivery_date') {
                // Format dates for display in history
                oldValue = oldValue ? format(new Date(oldValue), 'dd.MM.yyyy') : '';
                newValue = newValue ? format(new Date(newValue), 'dd.MM.yyyy') : '';
            }


            changes.push({
                print_run_id: originalData.id,
                user_name: user.full_name,
                action: 'updated',
                details: `Поле "${fieldLabels[key]}" изменено`,
                old_value: String(oldValue || ''),
                new_value: String(newValue || ''),
            });
        }
    }

    try {
        await PrintRun.update(originalData.id, updatedData);
        if (changes.length > 0) {
          await PrintRunHistory.bulkCreate(changes);
        }
        await loadData(); 
        setIsEditModalOpen(false);
        setEditingPrintRun(null);
        // If we were editing from a view modal, update the view modal's printRun
        if (isViewModalOpen && viewingPrintRun && viewingPrintRun.id === originalData.id) {
          setViewingPrintRun({ ...viewingPrintRun, ...updatedData });
        }
    } catch (error) {
        console.error("Failed to save changes:", error);
        alert("Произошла ошибка при сохранении изменений");
    }
  };

  const getBookTitle = (printRun) => {
    const bookId = printRun.book_id;
    
    if (!bookId) {
      console.warn('No book_id found for print run:', printRun);
      return 'Неизвестная книга';
    }
    
    const book = books.find(b => b.id === bookId);
    
    if (!book) {
      console.warn('Book not found for bookId:', bookId, 'Available books:', books.map(b => ({ id: b.id, title: b.title })));
      return 'Неизвестная книга';
    }
    
    return book.title;
  };

  const getWarehouseName = (warehouseId) => {
    if (!warehouseId) {
      return 'Неизвестный склад';
    }
    
    const warehouse = warehouses.find(w => w.id === warehouseId);
    return warehouse ? warehouse.name : 'Неизвестный склад';
  };

  return (
    <div className="min-h-screen gradient-bg p-6">
      <div className="max-w-7xl mx-auto space-y-6">
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
          <div>
            <h1 className="text-3xl font-bold text-slate-900">{t('print_runs.title')}</h1>
            <p className="text-slate-600 font-medium mt-1">{t('print_runs.subtitle')}</p>
          </div>
          <Button
            onClick={() => setShowCreateModal(true)}
            className="bg-amber-600 hover:bg-amber-700 text-white font-medium px-6"
          >
            <Plus className="w-4 h-4 mr-2" />
            {t('print_runs.new_print_run')}
          </Button>
        </div>

        <Card className="elegant-card">
          <CardContent className="p-6">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400 w-4 h-4" />
              <Input
                placeholder={t('print_runs.search_placeholder')}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 border-slate-200 focus:border-amber-500 focus:ring-amber-500"
              />
            </div>
          </CardContent>
        </Card>

        <Card className="elegant-card overflow-hidden">
          <CardContent className="p-0">
            <Table>
              <TableHeader className="bg-slate-50">
                <TableRow>
                  <TableHead className="font-bold text-slate-900">{t('print_runs.table.print_run_number')}</TableHead>
                  <TableHead className="font-bold text-slate-900">{t('print_runs.table.book')}</TableHead>
                  <TableHead className="font-bold text-slate-900">{t('print_runs.table.type')}</TableHead>
                  <TableHead className="font-bold text-slate-900">{t('print_runs.table.isbn')}</TableHead>
                  <TableHead className="font-bold text-slate-900">{t('print_runs.table.quantity')}</TableHead>
                  <TableHead className="font-bold text-slate-900">{t('print_runs.table.warehouse')}</TableHead>
                  <TableHead className="font-bold text-slate-900">{t('print_runs.table.status')}</TableHead>
                  <TableHead className="font-bold text-slate-900">{t('print_runs.table.cost')}</TableHead>
                  <TableHead className="font-bold text-slate-900 text-right">{t('print_runs.table.actions')}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {isLoading ? (
                  <TableRow>
                    <TableCell colSpan={9} className="text-center py-12 text-slate-500">
                      <div className="flex flex-col items-center gap-2">
                        <Printer className="w-12 h-12 text-slate-300 animate-pulse" />
                        <p className="font-medium">{t('print_runs.loading')}</p>
                      </div>
                    </TableCell>
                  </TableRow>
                ) : filteredPrintRuns.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={9} className="text-center py-12 text-slate-500">
                      <div className="flex flex-col items-center gap-2">
                        <Printer className="w-12 h-12 text-slate-300" />
                        <p className="font-medium">{t('print_runs.not_found')}</p>
                        <p className="text-sm">{t('print_runs.create_first')}</p>
                      </div>
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredPrintRuns.map((printRun) => {
                    const StatusIcon = statusIcons[printRun.status] || Clock;
                    return (
                      <TableRow key={printRun.id} className="hover:bg-slate-50/50">
                        <TableCell className="font-medium text-slate-900">
                          {printRun.document_number || printRun.id}
                        </TableCell>
                        <TableCell>
                          <div>
                            <p className="font-medium text-slate-900">{getBookTitle(printRun)}</p>
                            <p className="text-sm text-slate-500">{format(new Date(printRun.document_date || printRun.created_date), 'dd.MM.yyyy')}</p>
                          </div>
                        </TableCell>
                        <TableCell>
                          <Badge variant="outline" className={printRun.type === 'main_print' ? 'border-blue-200 text-blue-800' : 'border-purple-200 text-purple-800'}>
                            {printRun.type === 'main_print' ? t('print_runs.types.main_print') : t('print_runs.types.reprint')}
                          </Badge>
                        </TableCell>
                        <TableCell className="font-mono text-sm">
                          {printRun.isbn || '-'}
                        </TableCell>
                        <TableCell className="font-semibold text-slate-900">
                          {printRun.quantity.toLocaleString()} {t('inventory.books.units.copies')}
                        </TableCell>
                        <TableCell className="text-slate-600">
                          {getWarehouseName(printRun.warehouse_id)}
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <Badge className={`${statusColors[printRun.status] || statusColors.planned} border font-medium`}>
                              <StatusIcon className="w-3 h-3 mr-1" />
                              {printRun.status === 'planned' ? t('print_runs.status.planned') :
                               printRun.status === 'in_progress' ? t('print_runs.status.in_progress') : 
                               printRun.status === 'completed' ? t('print_runs.status.completed') :
                               printRun.status === 'delivered' ? t('print_runs.status.delivered') : 
                               printRun.status === 'cancelled' ? t('print_runs.status.cancelled') :
                               // Для обратной совместимости
                               printRun.status === 'draft' ? t('print_runs.status.draft') :
                               printRun.status === 'in_production' ? t('print_runs.status.in_production') : 
                               t('print_runs.status.planned')}
                            </Badge>
                            {(printRun.status === 'planned' || printRun.status === 'draft') && (
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleStatusChange(printRun.id, 'in_progress')}
                                className="text-amber-600 hover:text-amber-800"
                              >
                                {t('print_runs.actions.start_print')}
                              </Button>
                            )}
                            {(printRun.status === 'in_progress' || printRun.status === 'in_production') && (
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleStatusChange(printRun.id, 'completed')}
                                className="text-green-600 hover:text-green-800"
                              >
                                {t('print_runs.actions.complete')}
                              </Button>
                            )}
                            {printRun.status === 'completed' && (
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleStatusChange(printRun.id, 'delivered')}
                                className="text-blue-600 hover:text-blue-800"
                              >
                                {t('print_runs.actions.deliver')}
                              </Button>
                            )}
                          </div>
                        </TableCell>
                        <TableCell className="font-bold text-slate-900">
                          {currencySymbol}{((printRun.cost_per_unit || 0) * printRun.quantity).toLocaleString()}
                        </TableCell>
                        <TableCell className="text-right">
                            <div className="flex justify-end gap-1">
                                <Button 
                                    variant="ghost" 
                                    size="icon" 
                                    onClick={() => handleViewClick(printRun)}
                                    className="text-slate-500 hover:text-slate-800"
                                >
                                    <Eye className="w-4 h-4"/>
                                </Button>
                                <Button 
                                    variant="ghost" 
                                    size="icon" 
                                    onClick={() => handleEditClick(printRun)} 
                                    className="text-slate-500 hover:text-slate-800"
                                >
                                    <Edit className="w-4 h-4" />
                                </Button>
                            </div>
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </CardContent>
        </Card>

        <PrintRunForm
          isOpen={showCreateModal}
          onClose={() => setShowCreateModal(false)}
          onSubmit={handlePrintRunCreate}
          books={books}
          warehouses={warehouses}
          printRuns={printRuns}
        />
        
        <EditPrintRunModal 
            isOpen={isEditModalOpen}
            onClose={() => setIsEditModalOpen(false)}
            onSave={handleSaveChanges}
            printRun={editingPrintRun}
            allBooks={books}
            allWarehouses={warehouses}
        />

        <ViewPrintRunModal 
            isOpen={isViewModalOpen}
            onClose={() => setIsViewModalOpen(false)}
            onEdit={handleEditFromView}
            printRun={viewingPrintRun}
            book={books.find(b => b.id === viewingPrintRun?.book_id)}
            warehouse={warehouses.find(w => w.id === viewingPrintRun?.warehouse_id)}
        />
      </div>
    </div>
  );
}
